<template>
  <form @submit.prevent="handleSubmit" class="side-wrapper side-wrapper-flex">
    <!-- Header -->
    <div class="side-header">
      <div class="side-wrapper-subtitle">{{ generalStore.viewHeader }}</div>
      <h1 v-if="form.id">{{ $gettext("Edit event") }} '{{ form.name }}'</h1>
      <h1 v-else>{{ $gettext("Create event") }}</h1>
    </div>

    <!-- Body -->
    <div class="side-body" v-if="form">
      <div v-if="modules.TICKETING_INTEGRATION">
        <!-- Ticketing event -->
        <NiceDropdown
          :title="$gettext('Ticketing event')"
          v-model="form.ticketingEvent"
          :searchFunction="searchTicketingEvents"
          @change="onTicketingEventSelected"
          nullable
          valueName="name"
        >
          <template #option="{ item }">
            <div>
              <div>{{ item.name }}</div>
              <small>{{ formatDateWithTime(item.start_time, false, true) }}</small>
            </div>
          </template>
        </NiceDropdown>

        <hr class="hr-small" />
      </div>

      <!-- Name -->
      <NiceInput :title="$gettext('Name')" v-model="form.name" type="text" :required="true" />

      <!-- Start time -->
      <NiceDate :title="$gettext('Start time')" v-model="form.start_time" :required="true" />

      <!-- End time -->
      <NiceDate :title="$gettext('End time')" v-model="form.end_time" :required="true" />

      <!-- Cover photo -->
      <NiceUpload
        :title="$gettext('Cover photo')"
        v-model="form.cover_photo"
        accept="image/png, image/jpeg"
        :loading="loading"
        @change="onFileChange($event, 'cover_photo')"
      />

      <!-- Ticketing integration event -->
      <NiceInput
        :title="$gettext('Ticketing integration event ID')"
        v-model="form.ticketing_integration_event_id"
        type="number"
        v-if="modules.TICKETING_INTEGRATION"
        :disabled="true"
      />

      <!-- Use A4 template -->
      <div v-if="form.ticket_use_a4_template">
        <hr />

        <!-- Ticket header photo -->
        <NiceUpload
          :title="$gettext('Ticket header photo')"
          v-model="form.ticket_header_image"
          accept="image/png, image/jpeg"
          :loading="loading"
          @change="onFileChange($event, 'ticket_header_image')"
        />

        <!-- Ticket top left content -->
        <NiceTextarea :title="$gettext('Ticket top left content')" v-model="form.ticket_top_left_content" />

        <!-- Ticket bottom left content -->
        <NiceTextarea :title="$gettext('Ticket bottom left content')" v-model="form.ticket_bottom_left_content" />

        <!-- Ticket bottom right content -->
        <NiceTextarea :title="$gettext('Ticket bottom right content')" v-model="form.ticket_bottom_right_content" />

        <!-- Ticket email content -->
        <NiceTextarea :title="$gettext('Ticket email content')" v-model="form.ticket_email_content" />
      </div>
      <!-- <pre>{{ form }}</pre> -->
    </div>

    <!-- Footer -->
    <div class="side-footer">
      <NiceActions @cancel="cancel" :loading="loading" :showDelete="!!form.id" @delete="handleDelete" />
    </div>
  </form>
</template>

<script setup>
import { defineProps, onMounted, defineEmits, ref, computed } from "vue";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRouter } from "vue-router";
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const modules = computed(() => generalStore.modules);

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import { clone } from "@/plugins/query";
import { formatDateWithTime } from "@/plugins/date";

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});
const form = ref({
  name: null,
  cover_photo: null,
  start_time: null,
  end_time: null,
  ticketing_integration_event_id: null,
  ticket_header_image: null,
  ticket_top_left_content: null,
  ticket_bottom_left_content: null,
  ticket_bottom_right_content: null,
  ticket_email_content: null,
});
const loading = ref(false);
const organizationId = router.currentRoute.value.params.organizationId;

async function searchTicketingEvents(keyword) {
  return ApiService.internal.getTicketingEvents(organizationId, { search: keyword });
}

function onTicketingEventSelected(event) {
  if (!event) event = {};
  form.value.name = event.name;
  form.value.cover_photo = event.cover_photo;
  form.value.start_time = event.start_time;
  form.value.end_time = event.end_time;
  form.value.ticketing_integration_event_id = event.id;
}

async function handleSubmit() {
  loading.value = true;
  try {
    if (form.value.id) {
      form.value = await ApiService.events.update(organizationId, form.value);
      CommonService.success($gettext("Event was updated"));
    } else {
      form.value = await ApiService.events.create(organizationId, form.value);
      CommonService.success($gettext("Event was created"));
    }
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function handleDelete() {
  loading.value = true;
  try {
    form.value = await ApiService.events.delete(organizationId, form.value.id);
    CommonService.success($gettext("Event was deleted"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit("cancel");
}

async function onFileChange(file, field) {
  if (!file) return (form.value[field] = null);
  if (!file[0]) return (form.value[field] = null);
  if (!["image/jpeg", "image/png"].includes(file[0].type)) {
    CommonService.error($gettext("Wrong file format. Only .jpg and .png files are allowed."));
    return (form.value[field] = null);
  }

  loading.value = true;
  try {
    const response = await ApiService.storage.uploadPhoto(file[0], CommonService.photoCompressionTypes.COVER);
    form.value[field] = response.file;
  } catch (error) {
    ErrorService.showError(error);
    form.value[field] = null;
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  const eventId = router.currentRoute.value.query.id;
  if (eventId == "new") return;
  if (props.data) return (form.value = clone(props.data));

  try {
    form.value = await ApiService.events.get(organizationId, eventId);
  } catch (e) {
    emit("cancel");
  }
});
</script>
